import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import './CandidatesPage.css';

const CRITERIA_KEYS = [
  { id: 'Understanding', name: 'Understanding' },
  { id: 'coherence', name: 'Coherence' },
  { id: 'connection', name: 'Connection' },
  { id: 'clarification', name: 'Clarification' },
  { id: 'concision', name: 'Concision' },
  { id: 'conscious', name: 'Awareness' },
  { id: 'confidence', name: 'Confidence' },
];

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'https://api.avtechbmt.cloud/api/candidates';

const totalOf = (scores = {}) =>
  Object.values(scores).reduce((a, b) => a + Number(b || 0), 0);

const scoreTone = (v) => {
  if (v >= 4) return 'high';
  if (v === 3) return 'mid';
  if (v > 0) return 'low';
  return 'empty';
};

export default function CandidatesPage({
  students = [],
  setStudents,
  onViewReports,
  onRefresh,
}) {
  const { token } = useAuth();
  const [savingIds, setSavingIds] = useState(() => new Set());
  const [banner, setBanner] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  
  // Local state to hold active scoring data per candidate
  const [localScores, setLocalScores] = useState({});
  const [localRemarks, setLocalRemarks] = useState({});

  const debounceTimers = useRef({});
  const saveResetTimers = useRef({});

  const getAuthHeaders = useCallback(
    () => ({
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
    [token]
  );

  const safeParseResponse = async (res) => {
    const contentType = res.headers.get('content-type') || '';
    const textData = await res.text();
    if (!res.ok || !contentType.includes('application/json')) {
      if (textData.startsWith('<!DOCTYPE') || textData.includes('<html')) {
        throw new Error(`Server returned HTML instead of JSON (${res.status}).`);
      }
      throw new Error(`API request failed (${res.status}): ${textData}`);
    }
    try {
      return JSON.parse(textData);
    } catch {
      throw new Error('Invalid JSON received from server.');
    }
  };

  const markSaving = (id, on) => {
    setSavingIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  /* ----------------------------- API: create Candidate ----------------------------- */
  const addStudent = async () => {
    setBanner(null);
    try {
      const res = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: `Candidate #${students.length + 1}`,
        }),
      });
      const data = await safeParseResponse(res);
      if (data.success) {
        setStudents([data.data, ...students]);
        setBanner({ type: 'success', message: 'Candidate added.' });
        setTimeout(() => setBanner(null), 2500);
      }
    } catch (err) {
      setBanner({ type: 'error', message: `Create failed: ${err.message}` });
    }
  };

  /* ----------------------------- API: delete Candidate ----------------------------- */
  const removeStudent = async (id) => {
    setConfirmDeleteId(null);
    setBanner(null);
    try {
      const res = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await safeParseResponse(res);
      if (data.success) {
        setStudents(students.filter((s) => s._id !== id));
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      setBanner({ type: 'error', message: `Delete failed: ${err.message}` });
    }
  };

  /* ----------------------------- API: update Candidate Name ----------------------------- */
  const updateCandidateAPI = async (id, payload) => {
    markSaving(id, true);
    try {
      const res = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await safeParseResponse(res);
      if (data.success) {
        setStudents((prev) =>
          prev.map((s) => (s._id === id ? { ...s, ...data.data } : s))
        );
        if (saveResetTimers.current[id]) clearTimeout(saveResetTimers.current[id]);
        saveResetTimers.current[id] = setTimeout(() => markSaving(id, false), 900);
      } else {
        markSaving(id, false);
      }
    } catch (err) {
      markSaving(id, false);
      setBanner({ type: 'error', message: `Save failed: ${err.message}` });
    }
  };

  const updateStudentField = (studentId, field, val) => {
    setStudents((prev) =>
      prev.map((s) => (s._id === studentId ? { ...s, [field]: val } : s))
    );
    const key = `${studentId}_${field}`;
    if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key]);
    debounceTimers.current[key] = setTimeout(() => {
      updateCandidateAPI(studentId, { [field]: val });
    }, 550);
  };

  /* -------------------------- Local Scoring Logic ------------------------ */
  const handleScoreChange = (studentId, criterion, val) => {
    setLocalScores((prev) => {
      const current = prev[studentId] || {};
      const currentVal = current[criterion] || 0;
      const newVal = currentVal === val ? 0 : val; // Toggle off if clicked again
      return {
        ...prev,
        [studentId]: { ...current, [criterion]: newVal }
      };
    });
  };

  const handleRemarksChange = (studentId, text) => {
    setLocalRemarks(prev => ({ ...prev, [studentId]: text }));
  };

  const getLocalScore = (studentId, criterion) => {
    return localScores[studentId]?.[criterion] || 0;
  };

  const getLocalTotal = (studentId) => {
    return totalOf(localScores[studentId]);
  };

  /* ----------------------------- API: Save Report ----------------------------- */
  const saveReport = async (studentId) => {
    markSaving(studentId, true);
    setBanner(null);

    const scoresToSave = localScores[studentId] || {};
    const remarksToSave = localRemarks[studentId] || '';

    try {
      const res = await fetch(`${API_BASE_URL}/${studentId}/reports`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          scores: scoresToSave,
          remarks: remarksToSave
        }),
      });

      const data = await safeParseResponse(res);
      
      if (data.success) {
        setBanner({ type: 'success', message: 'Evaluation report saved successfully!' });
        
        // Clear local scores and remarks for this candidate
        setLocalScores((prev) => ({ ...prev, [studentId]: {} }));
        setLocalRemarks((prev) => ({ ...prev, [studentId]: '' }));
        
        // Refresh the candidate list to update the report count
        if (onRefresh) onRefresh();
        
        setTimeout(() => setBanner(null), 3000);
      } else {
        throw new Error(data.message || 'Failed to save report');
      }
    } catch (err) {
      setBanner({ type: 'error', message: `Save failed: ${err.message}` });
    } finally {
      markSaving(studentId, false);
    }
  };

  /* ------------------------------- cleanup ------------------------------- */
  useEffect(
    () => () => {
      Object.values(debounceTimers.current).forEach(clearTimeout);
      Object.values(saveResetTimers.current).forEach(clearTimeout);
    },
    []
  );

  return (
    <div className="candidate-page">
      <header className="cv-toolbar">
        <div className="cv-toolbar-left">
          <h1 className="cv-title">Candidate Evaluation Workspace</h1>
          <p className="cv-subtitle">
            Manage candidates, input evaluations, and view historical reports.
          </p>
        </div>

        <div className="cv-toolbar-right">
          <button type="button" className="cv-btn cv-btn-primary" onClick={addStudent}>
            + Add Candidate
          </button>
        </div>
      </header>

      {banner && (
        <div className={`cv-banner cv-banner-${banner.type}`} role="alert">
          {banner.type === 'error' ? '⚠️ ' : '✅ '}
          {banner.message}
        </div>
      )}

      <section className="cv-list" aria-label="Candidates">
        {students.length === 0 ? (
          <div className="cv-empty">
            No candidates yet. Click <strong>+ Add Candidate</strong> to begin.
          </div>
        ) : (
          students.map((student) => {
            const isSaving = savingIds.has(student._id);
            const isConfirming = confirmDeleteId === student._id;
            const reportCount = student.reportCount ?? 0;
            const currentTotal = getLocalTotal(student._id);

            return (
              <article key={student._id} className="cv-card">
                <div className="cv-card-head">
                  <div
                    className="cv-avatar"
                    aria-hidden="true"
                    onClick={() => onViewReports(student._id)}
                    title="View Historical Reports"
                  >
                    {(student.name || '?').trim().charAt(0).toUpperCase()}
                  </div>

                  <div className="cv-card-title">
                    <input
                      type="text"
                      className="cv-name-input"
                      value={student.name || ''}
                      onChange={(e) =>
                        updateStudentField(student._id, 'name', e.target.value)
                      }
                      placeholder="Candidate name"
                      aria-label="Candidate name"
                    />
                    <div className="cv-card-sub">
                      <span className="cv-chip cv-chip-soft">
                        {reportCount} {reportCount === 1 ? 'report' : 'reports'}
                      </span>
                      <span
                        className={`cv-save-state ${isSaving ? 'is-saving' : 'is-saved'}`}
                        aria-live="polite"
                      >
                        {isSaving ? 'Saving…' : 'Saved ✓'}
                      </span>
                    </div>
                  </div>

                  {/* View Reports Button */}
                  <button
                    type="button"
                    className="cv-score-badge"
                    onClick={() => onViewReports(student._id)}
                    aria-label="View historical reports"
                  >
                    <strong>{reportCount}</strong>
                    <span>{reportCount === 1 ? 'report' : 'reports'}</span>
                    <em>History →</em>
                  </button>

                  <div className="cv-card-menu">
                    {isConfirming ? (
                      <>
                        <button
                          type="button"
                          className="cv-confirm-yes"
                          onClick={() => removeStudent(student._id)}
                        >
                          Delete
                        </button>
                        <button
                          type="button"
                          className="cv-confirm-no"
                          onClick={() => setConfirmDeleteId(null)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="cv-remove-btn"
                        onClick={() => setConfirmDeleteId(student._id)}
                        aria-label="Remove candidate"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>

                {/* Inline Scoring Interface */}
                <div className="cv-criteria-scoring">
                  {CRITERIA_KEYS.map((crit) => {
                    const val = getLocalScore(student._id, crit.id);
                    return (
                      <div key={crit.id} className="cv-criteria-line">
                        <span className="cv-criteria-label">{crit.name}</span>
                        <div className="cv-rating-group">
                          {[1, 2, 3, 4, 5].map((v) => (
                            <button
                              key={v}
                              type="button"
                              className={`cv-rating-btn tone-${scoreTone(v)} ${
                                val === v ? 'is-active' : ''
                              }`}
                              onClick={() => handleScoreChange(student._id, crit.id, v)}
                              aria-pressed={val === v}
                            >
                              {v}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Remarks Section */}
                <div className="cv-remarks">
                  <label className="cv-remarks-label" htmlFor={`remarks-${student._id}`}>
                    Evaluation Remarks
                  </label>
                  <textarea
                    id={`remarks-${student._id}`}
                    className="cv-remarks-input"
                    value={localRemarks[student._id] || ''}
                    onChange={(e) => handleRemarksChange(student._id, e.target.value)}
                    placeholder="Record custom observations for this evaluation session…"
                    rows={2}
                  />
                </div>

                {/* Submit Evaluation Button */}
                <div className="cv-submit-eval-row">
                  <span className="cv-eval-total">Total: <strong>{currentTotal} / 35</strong></span>
                  <button 
                    type="button" 
                    className="cv-btn cv-btn-primary"
                    onClick={() => saveReport(student._id)}
                    disabled={isSaving || currentTotal === 0}
                  >
                    {isSaving ? 'Saving…' : 'Save as New Report'}
                  </button>
                </div>

              </article>
            );
          })
        )}
      </section>
    </div>
  );
}